import type { DayDetail, Station, Summary } from './api.ts';
import { el, escapeHtml, formatDateFull, formatTime, nl, temp } from './components/ui.ts';

/**
 * De kaart kleurt mee met de temperatuur: ijsblauw bij vorst, het vertrouwde
 * crème rond 15 °C, perzik en dieproze in de hitte. Elke halte draagt haar eigen
 * inkt en lijnkleuren, want een kleur die op crème werkt is op ijsblauw te zwak;
 * tussen twee haltes wordt lineair gemengd zodat één graad verschil nooit een
 * sprong geeft.
 *
 * De achtergrond blijft overal licht — het contrast van de inkt op de donkerste
 * halte is nog altijd ruim boven 7:1 — zodat de tekst leesbaar blijft en alleen
 * de orbs en lijnen de temperatuur echt luid maken.
 */
type Ramp = {
  t: number;
  /** Achtergrondverloop, van boven naar onder. */
  bg: [string, string];
  /** De grote warme orb, drie stops. */
  orb: [string, string, string];
  /** De twee kleinere, donkere orbs. */
  cool: string;
  ink: string;
  /** Stipkleur voor het dagmaximum en het dagminimum. */
  hot: string;
  cold: string;
  /** Staafjes van de neerslag langs de bodem van de curve. */
  rain: string;
};

/*
 * De haltes dragen de Navy-signatuur: amber en koraal in de warmte, violet in de
 * koude. Het ontwerpbestand port dit canvas niet mee — het staat er nog met de
 * kleuren van de vorige stijl in, en met een `var(--surf)` als vulkleur, wat op
 * een canvas niets betekent. De tinten hieronder zijn dus met de hand naar Navy
 * gehaald en opnieuw gemeten: `node web/tools/share-check.mjs`.
 */
const RAMP: Ramp[] = [
  { t: -10, bg: ['#e6eefb', '#d1e1f6'], orb: ['#a8cbff', '#6f83e6', '#333a86'], cool: '#1f2a5c', ink: '#10203f', hot: '#c02f4a', cold: '#5a34b0', rain: '#3f5fb0' },
  { t: 0, bg: ['#eaf2fd', '#d9e9fa'], orb: ['#9dc6ff', '#7183e8', '#3d4494'], cool: '#2a3670', ink: '#12234f', hot: '#c62f47', cold: '#6338bb', rain: '#4162b6' },
  { t: 8, bg: ['#f1f3fc', '#e2e8fa'], orb: ['#ffc38c', '#9a8bf0', '#7a5ce0'], cool: '#41456f', ink: '#1b2559', hot: '#cf3346', cold: '#6f34c0', rain: '#4568bd' },
  { t: 15, bg: ['#fdf4f0', '#fbe7e2'], orb: ['#ffb066', '#ff8a8a', '#ff6b6b'], cool: '#41456f', ink: '#1b2559', hot: '#cf3346', cold: '#7b2fb8', rain: '#4a6fc4' },
  { t: 22, bg: ['#fff2e6', '#ffe0d1'], orb: ['#ffa64d', '#ff7a72', '#f4564f'], cool: '#5b3f96', ink: '#3a1638', hot: '#c22b3c', cold: '#7330b0', rain: '#3f63b4' },
  { t: 28, bg: ['#ffe9dc', '#ffd3c1'], orb: ['#ff8c33', '#ff6350', '#e0342f'], cool: '#7c2358', ink: '#45102c', hot: '#b3242f', cold: '#6a2aa6', rain: '#3a5aa6' },
  { t: 35, bg: ['#ffdccb', '#ffc2ae'], orb: ['#ff6a1f', '#ff3f2e', '#c8140f'], cool: '#8e1f4f', ink: '#4a0f22', hot: '#a01722', cold: '#5f2496', rain: '#33509a' },
];

function mix(a: string, b: string, k: number): string {
  const parse = (h: string) => [1, 3, 5].map((i) => Number.parseInt(h.slice(i, i + 2), 16));
  const [r1, g1, b1] = parse(a) as [number, number, number];
  const [r2, g2, b2] = parse(b) as [number, number, number];
  const to = (x: number, y: number) =>
    Math.round(x + (y - x) * k)
      .toString(16)
      .padStart(2, '0');
  return `#${to(r1, r2)}${to(g1, g2)}${to(b1, b2)}`;
}

/** De kleuren voor deze temperatuur; null (geen meting) valt terug op de milde halte. */
function themeFor(value: number | null): Ramp {
  if (value === null) return RAMP[3]!;
  const first = RAMP[0]!;
  const last = RAMP[RAMP.length - 1]!;
  if (value <= first.t) return first;
  if (value >= last.t) return last;

  const hi = RAMP.findIndex((s) => s.t >= value);
  const a = RAMP[hi - 1]!;
  const b = RAMP[hi]!;
  const k = (value - a.t) / (b.t - a.t);
  return {
    t: value,
    bg: [mix(a.bg[0], b.bg[0], k), mix(a.bg[1], b.bg[1], k)],
    orb: [mix(a.orb[0], b.orb[0], k), mix(a.orb[1], b.orb[1], k), mix(a.orb[2], b.orb[2], k)],
    cool: mix(a.cool, b.cool, k),
    ink: mix(a.ink, b.ink, k),
    hot: mix(a.hot, b.hot, k),
    cold: mix(a.cold, b.cold, k),
    rain: mix(a.rain, b.rain, k),
  };
}

/**
 * Bouwt een deelbare afbeelding van 1200 × 630 — het formaat dat sociale media
 * en chat-apps als voorbeeld tonen.
 *
 * Met de hand op een canvas getekend en niet met een screenshot-bibliotheek: het
 * is een vaste compositie van een handvol getallen plus één curve, en zo blijft
 * het één bestand zonder extra afhankelijkheid.
 *
 * `day` is de dag die de hero-curve op dat moment toont — vandaag, of een dag
 * waar je naartoe gebladerd bent. Is hij null, dan staan alleen de cijfers van
 * vandaag op de kaart.
 */
export async function buildShareImage(
  summary: Summary,
  station: Station,
  day: DayDetail | null,
): Promise<Blob> {
  // Wachten tot Poppins geladen is, anders tekent het canvas in de fallback.
  try {
    await document.fonts?.ready;
  } catch {
    /* niet alle browsers hebben document.fonts */
  }

  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  // Op een retina-scherm zou 1× zichtbaar zacht zijn; we tekenen 2× en schalen terug.
  const dpr = 2;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const g = canvas.getContext('2d')!;
  g.scale(dpr, dpr);

  /*
   * Welke dag de kaart beschrijft.
   *
   * Staat de curve op vandaag, dan gaat de kaart over "nu": de live meting
   * groot, met het tijdstip ernaast. Bladerde je terug, dan is er geen "nu"
   * meer — dan draagt het maximum van die dag het beeld, en komen ook de
   * dagcijfers uit die dag in plaats van uit de samenvatting.
   */
  const isToday = day === null || day.date === summary.today.date;
  const date = day?.date ?? summary.today.date;
  const min = isToday ? summary.today.min : day!.min;
  const max = isToday ? summary.today.max : day!.max;
  const minAt = isToday ? summary.today.minAt : day!.minAt;
  const maxAt = isToday ? summary.today.maxAt : day!.maxAt;
  // Beide bronnen tellen op dezelfde manier: /day voegt het dagaggregaat en de
  // halfuren net zo samen als /summary dat voor vandaag doet. Hier stond eerder
  // alleen de som van de natte halfuren, en die telde stiller te laag zodra er
  // een gat in de reeks zat — een deelkaart van gisteren noemde dan een ander
  // getal dan de site zelf, en die twee zie je nooit naast elkaar.
  const rainTotal = isToday ? summary.today_rain : day!.rainTotal;

  const shown = isToday ? (summary.now.temperature ?? summary.today.max) : max;
  const skin = themeFor(shown);

  const sky = g.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, skin.bg[0]);
  sky.addColorStop(1, skin.bg[1]);
  g.fillStyle = sky;
  g.fillRect(0, 0, W, H);

  // Drie bollen in de rechterbovenhoek. Ze stonden ooit ook in de hero; Navy
  // heeft ze daar weggehaald, maar hier dragen ze het beeld — een deelkaart is
  // één plaatje zonder pagina eromheen die met het weer kan meekleuren.
  const orb = g.createLinearGradient(760, 40, 1200, 420);
  orb.addColorStop(0, skin.orb[0]);
  orb.addColorStop(0.55, skin.orb[1]);
  orb.addColorStop(1, skin.orb[2]);
  g.beginPath();
  g.arc(1080, 150, 260, 0, Math.PI * 2);
  g.fillStyle = orb;
  g.fill();

  const cool = g.createLinearGradient(820, 130, 1060, 370);
  cool.addColorStop(0, mix(skin.cool, skin.orb[1], 0.35));
  cool.addColorStop(1, skin.cool);
  g.fillStyle = cool;
  g.beginPath();
  g.arc(940, 250, 120, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.arc(1088, 318, 86, 0, Math.PI * 2);
  g.fill();

  const ink = skin.ink;
  // Gemeten over −12…40 °C tegen de donkerste helft van het verloop: inkt
  // ≥ 9,82:1, soft ≥ 4,73:1, faint ≥ 3,47:1, de maximumlijn ≥ 4,07:1 en de
  // minimumlijn ≥ 5,86:1. Narekenen: `npm run share-palette --workspace=web`.
  const soft = mix(ink, skin.bg[1], 0.3);
  const faint = mix(ink, skin.bg[1], 0.42);
  const place = (station?.city ?? 'Weerstation').toUpperCase();
  const dateLabel = date ? formatDateFull(date).toUpperCase() : '';

  g.fillStyle = soft;
  g.font = '500 26px Poppins, system-ui, sans-serif';
  g.fillText(`${place}${dateLabel ? ` · ${dateLabel}` : ''}`, 72, 108);

  g.fillStyle = ink;
  g.font = '300 190px Poppins, system-ui, sans-serif';
  const reading = shown === null ? '—' : `${temp(shown)}°`;
  const readingWidth = g.measureText(reading).width;
  g.fillText(reading, 66, 288);

  // Naast het getal staat waar het vandaan komt: nu, het laatste meetmoment, of
  // — bij een dag uit het archief — dat het om het dagmaximum gaat.
  const measured = formatTime(summary.now.measuredAt);
  const source = !isToday
    ? 'hoogste die dag'
    : summary.now.temperature === null
      ? 'hoogste vandaag'
      : summary.now.stale && measured
        ? `laatst gemeten ${measured}`
        : measured
          ? `nu · ${measured}`
          : 'nu';
  g.font = '500 28px Poppins, system-ui, sans-serif';
  g.fillStyle = soft;
  g.fillText(source, 78 + readingWidth, 246);

  // Wat de dag gaf. Met de tijdstippen erbij als het past, anders zonder.
  const dayLine = (withTimes: boolean): string => {
    const bits: string[] = [];
    // De hittegolf loopt nu; bij een dag uit het archief zegt dat niets.
    if (isToday && summary.heat?.ongoing) bits.push(`hittegolf dag ${summary.heat.days}`);
    if (max !== null) {
      const at = withTimes ? formatTime(maxAt) : null;
      bits.push(`maximum ${temp(max)} °C${at ? ` om ${at}` : ''}`);
    }
    if (min !== null) {
      const at = withTimes ? formatTime(minAt) : null;
      bits.push(`minimum ${temp(min)} °C${at ? ` om ${at}` : ''}`);
    }
    if (rainTotal !== null) bits.push(`${nl(rainTotal)} mm regen`);
    return bits.join('  ·  ');
  };

  // Op de hoogte van deze regel duikt de orb tot x ≈ 918, dus de tekst stopt
  // ruim daarvoor en niet pas aan de marge. Liever kleiner dan korter: de
  // tijdstippen sneuvelen pas als 20px nog altijd niet past.
  const ROOM = 820;
  let line = dayLine(true);
  let size = 26;
  const fits = () => {
    g.font = `400 ${size}px Poppins, system-ui, sans-serif`;
    return g.measureText(line).width <= ROOM;
  };
  while (!fits() && size > 20) size -= 1;
  if (!fits()) {
    line = dayLine(false);
    size = 26;
    while (!fits() && size > 19) size -= 1;
  }
  g.fillStyle = soft;
  g.fillText(line, 72, 372);

  // Het verloop van de dag, dezelfde tekening als de curve in de hero.
  const curve = day === null ? null : drawDayCurve(g, day, skin, soft, faint);

  g.fillStyle = faint;
  g.font = '400 22px Poppins, system-ui, sans-serif';
  if (curve) {
    // Kort: welke dag het is staat bovenaan al, en rechts moet de herkomstregel
    // er nog naast passen.
    g.fillText(`meting per 5 minuten${curve.wet ? ' · neerslag per half uur' : ''}`, 72, 604);
  }
  g.textAlign = 'right';
  g.fillText('weerstation · eigen netatmo-archief', W - 72, 604);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas gaf geen afbeelding'))), 'image/png');
  });
}

/**
 * Het verloop van één dag onderaan de kaart: de vijfminutenreeks als lijn, de
 * dagextremen als stip met tijd en waarde, en de buien als staafjes langs de
 * bodem.
 *
 * Dezelfde tekening als de curve in de hero (sections/hero.ts), maar op canvas
 * en zonder de zonmarkeringen: een deelkaart wordt in een tijdlijn bekeken, niet
 * bestudeerd, en dan mag er maar één lijn om aandacht vragen. Ook het label bij
 * de zwaarste bui blijft weg — dat getal staat hierboven al in de dagregel.
 *
 * @returns null als er voor deze dag geen bruikbare reeks is; anders of er
 *   neerslag getekend werd, voor het bijschrift.
 */
function drawDayCurve(
  g: CanvasRenderingContext2D,
  day: DayDetail,
  skin: Ramp,
  soft: string,
  faint: string,
): { wet: boolean } | null {
  const points = day.intraday;
  if (points.length < 5) return null;

  const LEFT = 72;
  const RIGHT = 1128;
  const FLOOR = 552; // bodem van het dagvlak; hierop staan de staafjes
  const BAND = 96; // hoogte waarin de curve zelf beweegt
  const HEAD = 24; // lucht onder de bodem van de curve, voor de laagste stip

  const [yy, mm, dd] = day.date.split('-').map(Number);
  const dayStart = Math.floor(new Date(yy!, mm! - 1, dd!).getTime() / 1000);
  // Niet dayStart + 86 400: op de dag van de klokwissel duurt de dag 23 of 25
  // uur, en dan zou de curve scheef in het vlak liggen.
  const dayLength = Math.floor(new Date(yy!, mm! - 1, dd! + 1).getTime() / 1000) - dayStart;

  /*
   * De schaal bevat ook de dagextremen, niet alleen de reeks: het dagaggregaat
   * kan een ruimere dag beslaan dan wat er van de vijfminutenreeks bewaard is,
   * en dan zou de stip buiten het vlak vallen. Zie de langere toelichting bij
   * dezelfde afweging in sections/hero.ts.
   */
  const values = points.map((p) => p.value);
  const marked = [day.min, day.max].filter((v): v is number => v !== null);
  const lo = Math.min(...values, ...marked);
  const hi = Math.max(...values, ...marked);
  const span = Math.max(0.5, hi - lo);

  const X = (ts: number) => LEFT + ((ts - dayStart) / dayLength) * (RIGHT - LEFT);
  const Y = (v: number) => FLOOR - HEAD - ((v - lo) / span) * BAND;

  /*
   * Eerst de neerslag, zodat de lijn er overheen loopt.
   *
   * Vaste referentie in plaats van de dagpiek, net als in de hero: anders ziet
   * een dag met drie druppels er even nat uit als een onweersbui. Vol getekend
   * en niet doorschijnend — dan meet share-check.mjs precies de kleur die er
   * ook echt komt te staan.
   */
  const wet = day.intradayRain.filter((b) => b.value > 0);
  const peak = wet.reduce((best, b) => (best === null || b.value > best.value ? b : best), null as
    | { ts: number; value: number }
    | null);
  const full = Math.max(2, peak?.value ?? 0);
  const MAX_BAR = 27;
  const MIN_BAR = 4; // 0,1 mm moet een staafje zijn, geen haartje

  g.fillStyle = skin.rain;
  for (const bucket of wet) {
    const x = X(bucket.ts);
    const width = X(bucket.ts + 1800) - x;
    if (x + width < LEFT || x > RIGHT) continue;
    const height = MIN_BAR + (Math.min(bucket.value, full) / full) * (MAX_BAR - MIN_BAR);
    g.fillRect(x + 1.5, FLOOR - height, Math.max(1, width - 3), height);
  }

  g.beginPath();
  points.forEach((p, i) =>
    i ? g.lineTo(X(p.ts), Y(p.value)) : g.moveTo(X(p.ts), Y(p.value)),
  );
  g.strokeStyle = skin.ink;
  g.lineWidth = 3;
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.stroke();

  // De dagextremen als gekleurde stip met tijd en waarde: het maximum boven de
  // lijn, het minimum eronder, elk binnen de marges zodat een extreem vlak na
  // middernacht geen half label buiten de kaart duwt.
  for (const [iso, value, colour, above] of [
    [day.minAt, day.min, skin.cold, false],
    [day.maxAt, day.max, skin.hot, true],
  ] as const) {
    if (!iso || value === null) continue;
    const x = X(Math.floor(new Date(iso).getTime() / 1000));
    if (x < LEFT || x > RIGHT) continue;
    const y = Y(value);

    g.beginPath();
    g.arc(x, y, 7, 0, Math.PI * 2);
    g.fillStyle = colour;
    g.fill();

    const label = `${formatTime(iso) ?? ''} · ${temp(value)} °C`;
    g.font = '500 20px Poppins, system-ui, sans-serif';
    g.fillStyle = soft;
    g.textAlign = 'center';
    const half = g.measureText(label).width / 2;
    // Het minimum hoort onder zijn stip, maar ligt het diep in het vlak, dan zou
    // die regel tussen de staafjes en de tijdsticks belanden. Dan gaat hij boven.
    const baseline = above || y + 30 > FLOOR - 6 ? y - 18 : y + 30;
    g.fillText(label, Math.min(RIGHT - half, Math.max(LEFT + half, x)), baseline);
    g.textAlign = 'left';
  }

  // Middernacht, middag, middernacht. Genoeg om te zien wanneer de dag zijn
  // vorm kreeg, zonder er een as van te maken.
  g.font = '400 20px Poppins, system-ui, sans-serif';
  g.fillStyle = faint;
  g.fillText('00:00', LEFT, 578);
  g.textAlign = 'center';
  g.fillText('12:00', (LEFT + RIGHT) / 2, 578);
  g.textAlign = 'right';
  g.fillText('24:00', RIGHT, 578);
  g.textAlign = 'left';

  return { wet: wet.length > 0 };
}

/** Toont de afbeelding met knoppen om te downloaden of te delen. */
export function openShareDialog(
  blob: Blob,
  summary: Summary,
  station: Station,
  day: DayDetail | null,
): void {
  const url = URL.createObjectURL(blob);
  // De bestandsnaam en de kop dragen de dag die op de afbeelding staat, niet de
  // dag waarop je hem maakte.
  const stamp = day?.date ?? summary.today.date ?? new Date().toISOString().slice(0, 10);
  const filename = `weerstation-${stamp}.png`;

  const modal = el('div', 'modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const sheet = el('div', 'sheet');
  sheet.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px">
      <div>
        <div class="eyebrow">Deelbare afbeelding · 1200 × 630</div>
        <div style="font-size:20px;font-weight:400;margin-top:2px">
          Weerstation ${escapeHtml(station?.city ?? '')}, ${escapeHtml(formatDateFull(stamp))}
        </div>
      </div>
    </div>
    <img src="${url}" alt="Samenvatting van het weerstation als afbeelding">`;

  const close = el('button', 'table-toggle', 'Sluiten');
  close.type = 'button';
  sheet.firstElementChild?.append(close);

  const actions = el('div', 'actions');
  const download = el('a', 'btn-solid', 'Download PNG');
  download.href = url;
  download.download = filename;
  actions.append(download);

  const note = el('span', 'eyebrow');

  // navigator.share met bestanden werkt vooral op mobiel; op desktop valt het
  // terug op het klembord, en anders blijft Download over.
  const file = new File([blob], filename, { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] }) || navigator.clipboard?.write) {
    const shareBtn = el('button', 'btn-ghost', 'Delen of kopiëren');
    shareBtn.type = 'button';
    shareBtn.addEventListener('click', async () => {
      try {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: 'Weerstation' });
          return;
        }
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        note.textContent = 'Afbeelding naar het klembord gekopieerd.';
      } catch {
        note.textContent = 'Delen lukte niet — gebruik Download.';
      }
    });
    actions.append(shareBtn);
  }

  actions.append(note);
  sheet.append(actions);
  modal.append(sheet);

  const dismiss = () => {
    URL.revokeObjectURL(url);
    modal.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') dismiss();
  };

  close.addEventListener('click', dismiss);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) dismiss();
  });
  document.addEventListener('keydown', onKey);

  document.body.append(modal);
  close.focus();
}
